import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';

export const CONTACT = {
  company: 'Red Pill Digital Solutions (Pvt) Ltd',
  address: 'No. 09, Balapokuna Road, Kirulapone, Colombo 05, Sri Lanka',
  email: 'hello@kidquest.lk',
  phone: '070 555 9558',
};

const OTHER_DOC = {
  '/terms-and-conditions': { to: '/privacy-policy', label: 'Privacy Policy' },
  '/privacy-policy': { to: '/terms-and-conditions', label: 'Terms & Conditions' },
};

const scrollToSection = (e, id) => {
  e?.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history?.replaceState(null, '', `#${id}`);
};

const LegalDocument = ({ path, eyebrow, title, subtitle, meta, intro, sections }) => {
  useEffect(() => {
    document.title = `${title} | KidQuest`;
    const hash = window.location?.hash?.slice(1);
    if (hash) document.getElementById(hash)?.scrollIntoView();
  }, [title]);

  const otherDoc = OTHER_DOC?.[path];
  const tocItems = sections?.filter((s) => !s?.sub);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="text-xl font-semibold tracking-tight">
            Kid<span className="text-primary">Quest</span>
          </span>
          {otherDoc && (
            <Link
              to={otherDoc?.to}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              {otherDoc?.label}
            </Link>
          )}
        </div>
      </header>

      <div className="bg-muted border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">{eyebrow}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-3">{title}</h1>
          {subtitle && <p className="text-muted-foreground mb-6">{subtitle}</p>}
          <dl className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            {meta?.map((item) => (
              <div key={item?.label}>
                <dt className="text-muted-foreground">{item?.label}</dt>
                <dd className="font-medium">{item?.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:grid lg:grid-cols-[240px_1fr] lg:gap-12">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Contents</p>
            <ul className="space-y-1 text-sm">
              {tocItems?.map((s) => (
                <li key={s?.id}>
                  <a
                    href={`#${s?.id}`}
                    onClick={(e) => scrollToSection(e, s?.id)}
                    className="block py-1 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {s?.heading ?? s?.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="min-w-0 max-w-3xl">
          {intro && (
            <div className="mb-10 rounded-lg border border-primary/20 bg-primary/5 p-4 sm:p-5 flex gap-3">
              <Icon name="Info" size={20} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{intro}</p>
            </div>
          )}

          {sections?.map((s) => (
            <section key={s?.id} id={s?.id} className={`scroll-mt-24 ${s?.sub ? 'mb-6' : 'mb-10'}`}>
              {s?.heading && (
                <h2 className="text-2xl font-semibold mb-6 pt-4 border-t border-border">{s?.heading}</h2>
              )}
              {s?.sub ? (
                <h4 className="text-base font-semibold mb-2">{s?.title}</h4>
              ) : (
                <h3 className="text-lg font-semibold text-primary mb-3">{s?.title}</h3>
              )}
              {s?.body?.map((p, i) => (
                <p key={i} className="leading-relaxed text-foreground/85 mb-3">{p}</p>
              ))}
              {s?.list && (
                <ul className="list-disc pl-5 space-y-1.5 leading-relaxed text-foreground/85">
                  {s?.list?.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              )}
            </section>
          ))}

          <div className="mt-12 rounded-lg border border-border bg-card p-6">
            <h3 className="font-semibold mb-3">Contact us</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {CONTACT.company}<br />{CONTACT.address}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-sm">
              <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 text-primary hover:underline">
                <Icon name="Mail" size={16} /> {CONTACT.email}
              </a>
              <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 text-primary hover:underline">
                <Icon name="Phone" size={16} /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </main>
      </div>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 justify-between text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {CONTACT.company}</span>
          <div className="flex gap-6">
            <Link to="/terms-and-conditions" className="hover:text-primary">Terms & Conditions</Link>
            <Link to="/privacy-policy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/support" className="hover:text-primary">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalDocument;
